import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import { ContentBlock } from "@/components/wpl/content-block/src";
import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

const CTASection = () => {
    const intl = useIntl();
    const signupLink = useLocalizedHref("/software/jira/free");
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_SOFTWARE,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });
    const ctaButtonText = intl.formatMessage({
        id: "JiraSoftwarePageTemplate.ctaSection.getItFree",
        defaultMessage: "Get it free"
    });

    const linkDataEventProps: DataEventProps = {
        "data-event-component": "linkButton",
        "data-event-container": "ctaSection",
        "data-event": "clicked",
        "data-product-key": "jiraSoftware",
        "data-label": ctaButtonText,
        "data-label-english": "Get it free",
        "data-track-event": "jiraSoftware.get-it-free-button-clicked"
    };

    return (
        <section
            css={css({
                "alignItems": "center",
                "backgroundColor": "#091e42",
                "color": "#ffffff",
                "display": "flex",
                "flexDirection": "column",
                "gap": "24px",
                "padding": "32px 0",
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                "h2": {
                    fontSize: "2.25rem",
                    fontFamily: "var(--bxp-charlie-display-font-family)",
                    fontWeight: "500"
                },
                [media.below.sm]: {
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    h2: {
                        fontSize: "1.5rem",
                        fontFamily: "var(--bxp-charlie-display-font-family)",
                        fontWeight: "500"
                    }
                },
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                ".features-link": {
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "svg": {
                        transition: "transform 0.25s ease-in-out",
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                        path: {
                            fill: "#fafbfc"
                        }
                    },

                    "&:focus": {
                        color: "#fafbfc"
                    },
                    "&:visited": {
                        color: "#fafbfc"
                    },
                    "&:hover": {
                        color: "#fafbfc",
                        textDecoration: "underline",
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                        svg: {
                            transform: `translateX(${token("space.100")})`
                        }
                    }
                }
            })}
        >
            <ContentBlock
                headerType="h2"
                headerText={intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.ctaSection.getStarted",
                    defaultMessage: "Get Started with Jira"
                })}
                orientation="vertical"
                ctaProps={{
                    buttonProps: {
                        text: intl.formatMessage({
                            id: "JiraSoftwarePageTemplate.ctaSection.getItFree",
                            defaultMessage: "Get it free"
                        }),
                        color: "yellow",
                        url: signupLink,
                        dataEventProps: linkDataEventProps,
                        onClick: openCrossFlow
                    }
                }}
                isCentered
            />
            <Link
                className="features-link"
                href={useLocalizedHref("/software/jira/features")}
                text={intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.ctaSection.seeFeatures",
                    defaultMessage: "See features"
                })}
                bgColor="bgBlackBlue"
                dataEventProps={linkDataEventProps}
            />
        </section>
    );
};

CTASection.displayName = "CTASection";

export default CTASection;
